// Registers Amplify's hosted-UI listener, which exchanges the `?code=` from a
// federated redirect for tokens. Amplify only attaches it via this side-effect
// import — nothing in aws-amplify/auth, signInWithRedirect, or
// @aws-amplify/ui-react pulls it in. Without this line the `inflightOAuth`
// marker that signInWithRedirect sets is never cleared, and every
// fetchAuthSession blocks on it forever.
import "aws-amplify/auth/enable-oauth-listener";
import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { sessionStorage } from "aws-amplify/utils";

import { USE_SSO } from "../../constants";

// The runtime shape delivered by public/config.js. Do not change this shape —
// config.js is deployed per environment and is not versioned with the app.
export interface RunePoolConfig {
  region?: string;
  userPoolId: string;
  userPoolWebClientId: string;
  domain?: string;
  oauth?: {
    domain: string;
    scope: string[];
    redirectSignIn: string;
    redirectSignOut: string;
    responseType: string;
  };
}

/**
 * Configures Amplify from a Rune runtime pool config, and pins Cognito tokens
 * to sessionStorage.
 *
 * Tokens MUST live in sessionStorage so that a session ends when the tab closes;
 * Amplify's default is localStorage. `Amplify.configure` resets the token
 * provider's storage, so setKeyValueStorage has to be re-applied after every
 * configure call — which is why the two are bound together here. Always
 * reconfigure through this function rather than calling `Amplify.configure`
 * directly.
 */
export function configureAmplify(pool: RunePoolConfig): void {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: pool.userPoolId,
        // config.js keys differ from Amplify's; this function is the only place
        // that translation happens.
        userPoolClientId: pool.userPoolWebClientId,
        ...(pool.oauth
          ? {
              loginWith: {
                oauth: {
                  domain: pool.oauth.domain,
                  scopes: pool.oauth.scope,
                  // Amplify accepts a list of allowed redirect URLs; config.js
                  // carries a single one per environment.
                  redirectSignIn: [pool.oauth.redirectSignIn],
                  redirectSignOut: [pool.oauth.redirectSignOut],
                  responseType: pool.oauth.responseType as "code" | "token"
                }
              }
            }
          : {})
      }
    }
  });

  cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);
}

/** Reads the two pool configs off the runtime global. */
function pools(): { user: RunePoolConfig; sso: RunePoolConfig } {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const config = (window as any).Rune.Carrot.config.aws;
  return {
    user: config.user.config,
    sso: config.sso.config
  };
}

/** Configures the email/password pool and clears the sticky SSO flag. */
export function configureUserPool(): void {
  configureAmplify(pools().user);
  window.sessionStorage.removeItem(USE_SSO);
}

/** Configures the SSO pool and sets the sticky SSO flag. */
export function configureSsoPool(): void {
  configureAmplify(pools().sso);
  window.sessionStorage.setItem(USE_SSO, "true");
}

/**
 * Boot-time pool selection. Call exactly once, at module scope.
 *
 * Amplify has to be configured before any auth call, but which pool to use is
 * only knowable from what the tab has already done: a `?code=` in the URL means
 * we are mid-Duo-redirect and must configure the SSO pool to complete the token
 * exchange, and the sticky flag covers the reloads that follow. Anything else
 * starts on the email/password pool. Both sign-in paths reconfigure explicitly
 * before they run, so a wrong guess here is recoverable.
 */
export function configureAmplifyForBoot(): void {
  const hasCode = new URLSearchParams(window.location.search).has("code");
  const stickyFlag = window.sessionStorage.getItem(USE_SSO) === "true";

  if (hasCode || stickyFlag) {
    configureSsoPool();
  } else {
    configureUserPool();
  }

  // The flag is session-scoped so it dies with the tab. Older builds kept it in
  // localStorage, where it outlived the session; clear any leftover copy.
  window.localStorage.removeItem(USE_SSO);
}
